import { useState, useEffect } from 'react';
import { MenuItem, Category, Subcategory } from '@/types/common';

export interface MenuModalProps {
  item?: MenuItem;
  categories: Category[];
  subcategories: Subcategory[];
  onSubmit: (data: Omit<MenuItem, 'id' | 'created_at'>) => Promise<void>;
  onClose: () => void;
}

export default function MenuModal({
  item,
  categories,
  subcategories,
  onSubmit,
  onClose
}: MenuModalProps) {
  const [name, setName] = useState(item?.name || '');
  const [description, setDescription] = useState(item?.description || '');
  const [price, setPrice] = useState(item?.price?.toString() || '');
  const [categoryId, setCategoryId] = useState(item?.category_id || '');
  const [subcategoryId, setSubcategoryId] = useState(item?.subcategory_id || '');
  const [active, setActive] = useState(item?.active ?? true);
  const [submitting, setSubmitting] = useState(false);

  // Filter subcategories based on selected category
  const availableSubcategories = subcategories.filter(
    sub => sub.category_id === categoryId
  );

  // Reset subcategory when category changes
  useEffect(() => {
    setSubcategoryId('');
  }, [categoryId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;

    try {
      setSubmitting(true);
      await onSubmit({
        name,
        description: description || null,
        price: parseFloat(price),
        category_id: categoryId,
        subcategory_id: subcategoryId || null,
        active
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="p-6 space-y-4">
      {/* Name */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-1">
          Name
        </label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="input w-full bg-white dark:bg-dark-bg text-gray-900 dark:text-dark-text-primary border-2 border-gray-200 dark:border-dark-border rounded-lg focus:border-primary-500 dark:focus:border-primary-500 px-4 h-11"
          required
        />
      </div>

      {/* Description */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-1">
          Description
        </label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className="textarea w-full h-24 bg-white dark:bg-dark-bg text-gray-900 dark:text-dark-text-primary border-2 border-gray-200 dark:border-dark-border rounded-lg focus:border-primary-500 dark:focus:border-primary-500 p-4 resize-none"
        />
      </div>

      {/* Price */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-1">
          Price
        </label>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <span className="text-gray-500 sm:text-sm">€</span>
          </div>
          <input
            type="number"
            step="0.01"
            min="0"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            className="input w-full pl-7 bg-white dark:bg-dark-bg text-gray-900 dark:text-dark-text-primary border-2 border-gray-200 dark:border-dark-border rounded-lg focus:border-primary-500 dark:focus:border-primary-500 px-4 h-11"
            required
          />
        </div>
      </div>

      {/* Category */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-1">
          Category
        </label>
        <select
          value={categoryId}
          onChange={(e) => setCategoryId(e.target.value)}
          className="select w-full bg-white dark:bg-dark-bg text-gray-900 dark:text-dark-text-primary border-2 border-gray-200 dark:border-dark-border rounded-lg focus:border-primary-500 dark:focus:border-primary-500 px-4 h-11"
          required
        >
          <option value="">Select a category</option>
          {categories.map((category) => (
            <option key={category.id} value={category.id}>
              {category.name}
            </option>
          ))}
        </select>
      </div>

      {/* Subcategory */}
      {availableSubcategories.length > 0 && (
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-1">
            Subcategory
          </label>
          <select
            value={subcategoryId}
            onChange={(e) => setSubcategoryId(e.target.value)}
            className="select w-full bg-white dark:bg-dark-bg text-gray-900 dark:text-dark-text-primary border-2 border-gray-200 dark:border-dark-border rounded-lg focus:border-primary-500 dark:focus:border-primary-500 px-4 h-11"
          >
            <option value="">No subcategory</option>
            {availableSubcategories.map((subcategory) => (
              <option key={subcategory.id} value={subcategory.id}>
                {subcategory.name}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Active Status */}
      <div className="flex items-center">
        <input
          type="checkbox"
          checked={active}
          onChange={(e) => setActive(e.target.checked)}
          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 dark:border-dark-border rounded"
        />
        <label className="ml-2 block text-sm text-gray-900 dark:text-dark-text-primary">
          Item is available
        </label>
      </div>

      {/* Footer */}
      <div className="flex justify-end space-x-3 pt-4">
        <button
          type="button"
          onClick={onClose}
          className="btn bg-white dark:bg-dark-bg hover:bg-gray-50 dark:hover:bg-dark-bg-light text-gray-700 dark:text-dark-text-secondary border-2 border-gray-200 dark:border-dark-border px-6 h-11 font-medium"
        >
          Cancel
        </button>
        <button 
          type="submit" 
          disabled={submitting}
          className="btn btn-primary px-6 h-11 font-medium"
        >
          {submitting ? (
            <>
              <i className="fas fa-spinner fa-spin mr-2" />
              Saving...
            </>
          ) : (
            'Save'
          )}
        </button>
      </div>
    </form>
  );
}